import { createApi } from "../../../../utils/createApi";
import { saveAs } from "file-saver";

export const SITES_OVERVIEW_FILENAME = "sites_overview.xlsx";
export const SITES_OVERVIEW_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// How often to ask whether the workbook is ready, and for how long before
// giving up. A big group (thousands of sites) takes a few minutes to build.
export const SITES_OVERVIEW_POLL_MS = 3000;
export const SITES_OVERVIEW_TIMEOUT_MS = 15 * 60 * 1000;

export type SitesOverviewProgress = "preparing" | "downloading";
// "downloading" carries the file URL when the file comes from S3, so the UI
// can offer a click-to-download fallback.
export type SitesOverviewProgressHandler = (
  progress: SitesOverviewProgress,
  url?: string
) => void;

type OverviewStatus =
  | { status: "ready"; url: string }
  | { status: "pending"; task_id: string }
  | { status: "failed"; task_id: string; detail?: string };

export class SitesOverviewFailedError extends Error {
  constructor(detail?: string) {
    super(detail || "The sites overview could not be built.");
    this.name = "SitesOverviewFailedError";
  }
}

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// Start a download of a Content-Disposition: attachment URL without a user
// gesture. By the time the workbook is ready the click that started it is
// minutes old, so file-saver's cross-origin path (a synthetic click on a
// target=_blank link) is treated as a popup and blocked. A hidden iframe
// navigation is not: the browser saves the attachment and the page stays.
export const startAttachmentDownload = (url: string) => {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("data-testid", "sites-overview-download");
  frame.style.display = "none";
  frame.src = url;
  document.body.appendChild(frame);
  // Long enough for the browser to begin the download on a slow link.
  setTimeout(() => frame.remove(), 60 * 1000);
};

const isXlsxResponse = (headers: Record<string, unknown> | undefined) =>
  String(headers?.["content-type"] ?? "")
    .toLowerCase()
    .startsWith(SITES_OVERVIEW_MIME_TYPE);

// "Download Sites Overview" (Parent Groups & Sites kebab menu): a workbook
// with a "Sites" sheet (one row per accessible site with its settings and
// camera rollups) and an "All Cameras" sheet (every camera on every site in
// scope). groupId narrows to that group's subtree; 0/undefined means full scope.
//
// The backend builds the workbook off-request (a large group takes minutes,
// longer than the load balancer allows a request to run) and answers 202
// {status: "pending", task_id} until it is in S3, then 200 {status: "ready",
// url}. This polls with the task_id until the file is ready, then saves it.
//
// An admin build from before that change (or a rollback) answers with the
// xlsx bytes themselves; that response is saved directly, so the two halves
// can ship in either order.
export const downloadSitesOverview = async (
  groupId?: number,
  onProgress?: SitesOverviewProgressHandler
) => {
  const AxiosUI = createApi("");
  const params = new URLSearchParams();
  if (groupId) {
    params.set("group_id", String(groupId));
  }
  const endpoint = () =>
    `site_settings_report/overview_xlsx/${
      params.toString() ? `?${params.toString()}` : ""
    }`;

  onProgress?.("preparing");
  const deadline = Date.now() + SITES_OVERVIEW_TIMEOUT_MS;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // arraybuffer rather than blob: the xlsx case wraps the bytes as-is and
    // the JSON case decodes them synchronously, with no FileReader.
    const response = await AxiosUI.get<ArrayBuffer>(endpoint(), {
      responseType: "arraybuffer",
    });
    if (isXlsxResponse(response.headers)) {
      onProgress?.("downloading");
      const blob = new Blob([response.data], {
        type: SITES_OVERVIEW_MIME_TYPE,
      });
      saveAs(blob, SITES_OVERVIEW_FILENAME);
      return null;
    }
    const body = JSON.parse(
      new TextDecoder().decode(response.data)
    ) as OverviewStatus;
    if (body.status === "ready") {
      onProgress?.("downloading", body.url);
      startAttachmentDownload(body.url);
      return body.url;
    }
    if (body.status === "failed") {
      throw new SitesOverviewFailedError(body.detail);
    }
    params.set("task_id", body.task_id);
    if (Date.now() >= deadline) {
      throw new SitesOverviewFailedError(
        "The sites overview is taking too long to build. Please try again later."
      );
    }
    await wait(SITES_OVERVIEW_POLL_MS);
  }
};
